import type { ReactNode } from 'react';
import { Group } from '@mantine/core';

import Linkv2 from '../link/Linkv2';
import MercuryButton from '../button/Button';

import type { Toast as ToastType, ToastLevel } from './types';

import CloseIcon from '@/assets/icons/content/close.svg';
import SuccessIcon from '@/assets/icons/content/success.svg';
import WarningIcon from '@/assets/icons/content/warning.svg';
import ErrorIcon from '@/assets/icons/content/error.svg';
import InfoIcon from '@/assets/icons/content/info.svg';

import classes from './Toast.module.css';

type ToastProps = ToastType;

const levelLabel: Record<ToastLevel, string> = {
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
};

const levelIcons: Record<ToastLevel, ReactNode> = {
  success: <SuccessIcon />,
  warning: <WarningIcon />,
  error: <ErrorIcon />,
  info: <InfoIcon />,
};

export const Toast = ({ message, clear, level, link, actions }: ToastProps) => (
  <button
    type="button"
    onClick={clear}
    className={`${classes.toast} ${classes[level]}`}
    data-toast
    data-toast-level={level}
  >
    <Group classNames={{ root: classes.icon }} data-toast-icon>
      {levelIcons[level]}
    </Group>

    <output className={classes.output} data-toast-output>
      <span className={classes.label} data-toast-label>
        {levelLabel[level]}
      </span>

      <span className={classes.message} data-toast-message>
        {message}

        {link && (
          <div className={classes.link}>
            <Linkv2 href={link.to} target="_blank">
              {link.text ?? link.to}
            </Linkv2>
          </div>
        )}
      </span>

      {actions && actions.length > 0 && (
        <div className={classes.actions}>
          {actions.map((action) => (
            <MercuryButton
              key={`${action.label}-${action.variant ?? 'primary'}`}
              size="xs"
              variant={action.variant === 'secondary' ? 'outline' : 'filled'}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
            >
              {action.label}
            </MercuryButton>
          ))}
        </div>
      )}
    </output>

    <CloseIcon className={classes.closeIcon} />
  </button>
);
